import { throttle } from '@utils/functional';
import { useRef } from 'react';
import type { UseInfiniteQueryResult } from '@tanstack/react-query';

export interface InfinitePage<TData, TCursor = number> {
	items: TData[];
	nextCursor?: TCursor;
}

/**
 * Helper function to extract the next cursor from a paginated response.
 * Pass this to the `getNextPageParam` option of `useInfiniteQuery`.
 *
 * @param lastPage The last page of data returned from the query
 * @returns The cursor for the next page, or undefined if there is no next page
 */
export const getNextPageParam = <TData, TCursor = number>(lastPage?: InfinitePage<TData, TCursor>) => lastPage && lastPage.nextCursor;

/**
 * A wrapper hook for tRPC infinite queries that simplifies pagination handling.
 *
 * This hook flattens paginated data into a single array and provides a throttled
 * `fetchNextPage` callback that prevents duplicate requests.
 *
 * It's important to pass getNextPageParam to the `useInfiniteQuery` options.
 *
 * Passing in the result of `useInfiniteQuery` instead of calling it here
 * significantly reduces the TS checking load.
 *
 * @param query The result of calling `trpc.routerName.methodName.useInfiniteQuery()`
 * @returns An object containing:
 *   - `data` - All items from all loaded pages, flattened into a single array
 *   - `fetchNextPage` - Throttled callback to fetch the next page of results
 *   - `hasNextPage` - Whether there are more pages to load
 *   - `isError` - Whether the query encountered an error
 *   - `isFetching` - Whether any page is currently being fetched
 *   - `isLoading` - Whether the initial page is loading
 *   - `isSuccess` - Whether the query has successfully loaded data
 *   - `query` - The original query result for advanced usage
 *
 * @example
 * ```tsx
 * import { getNextPageParam, useInfinite2 } from '~/hooks/useInfinite';
 * import { trpc } from '@utils/trpc';
 *
 * function JobsList() {
 *   const { data, fetchNextPage, isLoading, hasNextPage } = useInfinite(
 *     trpc.jobs.allForDashboard.useInfiniteQuery(
 *       { filters: { status: 'active' }, limit: 25 },
 *       { getNextPageParam }
 *     )
 *   );
 *
 *   if (isLoading) return <LoadingSpinner />;
 *
 *   return (
 *     <div>
 *       {data.map((job) => <JobCard key={job.id} job={job} />)}
 *       {hasNextPage && <button onClick={fetchNextPage}>Load More</button>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useInfinite<TData, TCursor = number>(query: UseInfiniteQueryResult<InfinitePage<TData, TCursor>>) {
	const data = query.isSuccess && query.data ? query.data.pages.flatMap((p?: InfinitePage<TData, TCursor>) => (p ? p.items : [])) : [];

	// Keep a ref to the latest query so the stable throttle closure always reads current values
	const queryRef = useRef(query);
	queryRef.current = query;

	// useRef ensures the throttle instance (and its timer) persists across renders
	const fetchNextPage = useRef(
		throttle(() => {
			if (!queryRef.current.isFetching && queryRef.current.hasNextPage) {
				queryRef.current.fetchNextPage();
			}
		})
	).current;

	return {
		data,
		fetchNextPage,
		hasNextPage: query.hasNextPage ?? false,
		isError: query.isError,
		isFetching: query.isFetching, // Are we getting something from the server?
		isLoading: query.isLoading, // Is this the initial load?
		isSuccess: query.isSuccess,
		query,
	};
}
